import React, { useEffect } from "react";
import { useParams } from "react-router-dom";

import { Grid, Box, Typography, Card, Avatar, CircularProgress, Alert } from "@mui/material";
import {
    CheckCircle as CheckCircleIcon,
    AccessTime as AccessTimeIcon,
    Rule as RuleIcon,
    Payments as PaymentsIcon,
    SyncProblem as SyncProblemIcon,
    MoreHoriz as MoreHorizIcon
} from "@mui/icons-material";

import { DataLayout, StatusAvatar, ProjectTimeline, PerformanceChart, StatusChip, BudgetCard } from "../../../shared/components";
import { formatCurrency } from "../../../shared/utils";

import { useStageGate } from "../hooks/useStageGate";
import { useRagStatus } from "../hooks/useRagStatus";
import { usePhase } from "../hooks/usePhase";
import { useCostTrend } from "../hooks/useCostTrend";
import { useOverviewStats } from "../hooks/useOverviewStats";

export const Dashboard: React.FC = () => {

    const { projectId } = useParams<{ projectId: string }>();

    const { isPending: isStageGateDetailsPending, stageGateData } = useStageGate(projectId);
    const { isPending: isRagStatusDetailsPending, ragStatusData } = useRagStatus(projectId);

    const { isPending: isOverviewStatsDetailsPending, overviewStatsData } = useOverviewStats(projectId);
    const { isPending: isCostTrendDetailsPending, costTrendData } = useCostTrend(projectId);

    const phaseData = usePhase();
    let isPhasesDetailsPending = phaseData.isPending;

    useEffect(() => {
        phaseData.mutateAsync(projectId);
    }, []);

    if (isPhasesDetailsPending || isRagStatusDetailsPending || isStageGateDetailsPending || isOverviewStatsDetailsPending || isCostTrendDetailsPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Grid container spacing={2} p={2}>

            <Grid size={12}>
                <DataLayout title="Project Timeline">
                    <ProjectTimeline steps={phaseData?.data?.data || []} />
                </DataLayout>
            </Grid>

            <Grid size={12}>
                <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>
                    {/* Stage Gate Status */}
                    <DataLayout title="Stage Gate Status">
                        <Box
                            display="flex"
                            gap={2}
                            flexDirection="column"
                            p={2}
                            sx={{
                                height: 250,
                                overflowY: 'auto'
                            }}
                        >
                            {stageGateData.length === 0 ? (
                                <Box my="auto">
                                    <Alert severity="warning">Stage Gate to be Defined</Alert>
                                </Box>
                            ) : (
                                stageGateData.map(
                                    (
                                        gate: {
                                            name: string;
                                            status: string;
                                            status_display_text: string;
                                            color: 'green' | 'red' | 'amber';
                                        },
                                        index: number
                                    ) => (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={2}
                                            flex={1}
                                            key={index}
                                        >
                                            <StatusAvatar
                                                bgcolor={
                                                    gate.color === 'green'
                                                        ? 'success.main'
                                                        : gate.color === 'amber'
                                                            ? 'warning.main'
                                                            : gate.color === 'red'
                                                                ? 'error.main'
                                                                : 'default'
                                                }
                                            >
                                                {gate.color === 'green' ? (
                                                    <CheckCircleIcon />
                                                ) : gate.color === 'amber' ? (
                                                    <MoreHorizIcon />
                                                ) : gate.color === 'red' ? (
                                                    <SyncProblemIcon />
                                                ) : (
                                                    <SyncProblemIcon />
                                                )}
                                            </StatusAvatar>
                                            <Typography variant="subtitle2" color="text.primary">
                                                {gate.name}
                                            </Typography>
                                            <StatusChip
                                                sx={{ ml: 'auto' }}
                                                label={gate.status_display_text}
                                                size="small"
                                                color={gate.color as 'red' | 'green' | 'amber'}
                                                variant="outlined"
                                            />
                                        </Box>
                                    )
                                )
                            )}
                        </Box>
                    </DataLayout>

                    {/* Critical Actions */}
                    <DataLayout title="Critical Actions">
                        <Box
                            mx={1}
                            my={2}
                            display="flex"
                            flexDirection="column"
                            justifyContent="start"
                            gap={2}
                            px={1}
                        >
                            {[
                                {
                                    label: 'Safety training completion',
                                    subLabel: 'Due in 3 days',
                                    status: 'High',
                                    color: 'red' as const,
                                },
                                {
                                    label: 'Safety training completion',
                                    subLabel: 'Due in 1 week',
                                    status: 'Medium',
                                    color: 'amber' as const,
                                },
                                {
                                    label: 'Vendor contract review',
                                    subLabel: 'Due in 2 weeks',
                                    status: 'Medium',
                                    color: 'amber' as const,
                                },
                                {
                                    label: 'Quality audit preparation',
                                    subLabel: 'Due in 3 weeks',
                                    status: 'Low',
                                    color: 'green' as const,
                                },
                            ].map((value, index) => (
                                <Box
                                    key={index}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap={1}
                                >
                                    <Box display="flex" flexDirection="column">
                                        <Typography variant="subtitle2" color="text.primary">
                                            {value.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {value.subLabel}
                                        </Typography>
                                    </Box>
                                    <StatusChip label={value.status} color={value.color} variant="filled" />
                                </Box>
                            ))}
                        </Box>
                    </DataLayout>

                    {/* RAG Status */}
                    <DataLayout title="RAG Status">
                        <Box
                            mx={1}
                            my={2}
                            display="flex"
                            flexDirection="column"
                            justifyContent="start"
                            gap={2}
                            px={1}
                        >
                            {['cost', 'time', 'quality'].map((value, index) => (
                                <Card
                                    key={index}
                                    variant="outlined"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderColor:
                                            ragStatusData[value] === 'green'
                                                ? 'success.main'
                                                : ragStatusData[value] === 'amber'
                                                    ? 'warning.main'
                                                    : 'error.main',
                                        gap: 2,
                                        px: 1,
                                        py: 1,
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            backgroundColor:
                                                ragStatusData[value] === 'green'
                                                    ? 'success.main'
                                                    : ragStatusData[value] === 'amber'
                                                        ? 'warning.main'
                                                        : 'error.main',
                                            borderRadius: 1,
                                        }}
                                    >
                                        {value === 'cost' ? (
                                            <PaymentsIcon />
                                        ) : value === 'time' ? (
                                            <RuleIcon />
                                        ) : (
                                            <AccessTimeIcon />
                                        )}
                                    </Avatar>
                                    <Box display="flex" flexDirection="column">
                                        <Typography variant="subtitle2">
                                            {value?.charAt(0).toUpperCase() + value?.slice(1)}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color={
                                                ragStatusData[value] === 'green'
                                                    ? 'success.main'
                                                    : ragStatusData[value] === 'amber'
                                                        ? 'warning.main'
                                                        : 'error.main'
                                            }
                                        >
                                            {ragStatusData[value]?.charAt(0).toUpperCase() + ragStatusData[value]?.slice(1)}
                                        </Typography>
                                    </Box>
                                </Card>
                            ))}
                        </Box>
                    </DataLayout>
                </Box>
            </Grid>

            <Grid size={12}>
                <Grid container spacing={2}>
                    {(() => {
                        const totalBudget = overviewStatsData?.total_budget || 0;
                        const spentToDate = overviewStatsData?.spent_to_date || 0;
                        const remainingBudget = totalBudget - spentToDate;
                        const forecastVariance = overviewStatsData?.forecast_variance || 0;
                        return [
                            { label: 'Total Budget', value: formatCurrency(totalBudget).replace('£', '').trim(), icon: 'Balance', color: 'green' as const },
                            { label: 'Spent to Date', value: formatCurrency(spentToDate).replace('£', '').trim(), icon: 'Cost', color: 'amber' as const },
                            { label: 'Remaining Budget', value: formatCurrency(remainingBudget).replace('£', '').trim(), icon: 'Money', color: 'green' as const },
                            { label: 'Forecast Variance', value: `${formatCurrency(forecastVariance).replace('£', '').trim()}`, icon: 'Trending', color: 'red' as const },
                        ].map((value, index) => (
                            <Grid size={3} key={index}>
                                <BudgetCard label={value.label} value={value.value} icon={value.icon} color={value.color} />
                            </Grid>
                        ));
                    })()}
                </Grid>
            </Grid>

            <Grid size={12}>
                <DataLayout title="Cost Trend Analysis">
                    <PerformanceChart data={costTrendData} />
                </DataLayout>
            </Grid>

        </Grid>
    );
};  